<script setup lang="ts">
    import type { SelectOption } from '~/components/shared/SelectInput.vue';
    import { useDatasourceEntries } from '~/composables/storyblok-proxy/useDatasourceEntries';
    import { useStories } from '~/composables/storyblok-proxy/useStories';

    const props = defineProps<{ blok: PageNewsOverviewStoryblok }>();
    const { lang } = useLanguage();
    const { t } = useI18n();
    const { renderToString } = useRichTextRenderer();

    const matchedConfig = useCurrentSectionConfig();

    const route = useRoute();
    const router = useRouter();
    const selectedCategory = computed(
        () => (Array.isArray(route.query.category) ? route.query.category[0] : route.query.category) || null
    );

    const canonicalUrlAbsolute = useCanonicalUrl(props.blok.canonicalLink);
    const { getReferenceUuid, normalizeNewsCardStory } = useResolvedNewsStory();
    const { isFachportal } = usePlatformMeta();
    const head = computed(() => {
        const seoTitle = props.blok.AI_SEO?.title ?? props.blok.seo?.title ?? '';

        return {
            title: `${seoTitle && seoTitle.length > 0 ? `${seoTitle} | ` : ''}News | ${
                isFachportal.value ? `${t('platform.prescriber')} | ` : ''
            }MEDICE Health Family`,
            htmlAttrs: {
                lang: lang.value
            },
            link: [{ rel: 'canonical' as const, href: canonicalUrlAbsolute.value }]
        };
    });
    useHead(head);

    const params = computed(() => {
        const segments = route.path.split('/').filter(Boolean);
        const root = decodeURIComponent(segments.at(-1) ?? '');
        const rootPath = matchedConfig.value?.sectionRootPath || '/';
        const starts_with = matchedConfig.value?.sectionRootPath === '/' ? `${root}` : `${rootPath}/${root}`;

        return {
            starts_with,
            resolve_relations: 'PageNewsReference.reference',
            filter_query: {
                component: {
                    in: 'PageNews,PageNewsReference'
                }
            }
        };
    });
    const { data: newsData } = await useStories<PageNewsStoryblok | PageNewsReferenceStoryblok>(params);

    const referencedNewsUuids = computed(() => {
        return [
            ...new Set(
                (newsData.value ?? []).map((story) => getReferenceUuid(story)).filter((uuid): uuid is string => !!uuid)
            )
        ];
    });

    const referencedNewsParams = computed(() => ({
        ...(referencedNewsUuids.value.length ? { by_uuids: referencedNewsUuids.value.join(',') } : {}),
        filter_query: {
            component: {
                in: 'PageNews'
            }
        }
    }));

    const referencedFetchOptions = computed(() => ({
        disabled: referencedNewsUuids.value.length === 0
    }));

    const { data: referencedNewsData } = await useStories<PageNewsStoryblok>(
        referencedNewsParams,
        referencedFetchOptions
    );

    const { data: datasourceData } = await useDatasourceEntries('news-categories');
    const datasourceEntries = computed(() => datasourceData.value || []);
    const categoryTranslations = computed(() => {
        const translations = new Map<string, string>();

        for (const element of datasourceEntries.value) {
            let translation = element.name;
            if (element.value) translation = element.value;
            if (element.dimension_value) translation = element.dimension_value;

            translations.set(element.name, translation);
        }

        return translations;
    });

    const sortOrder = ref('DESC');
    const sortingOptions = computed<SelectOption[]>(() => [
        {
            label: t('pageNews.sort.option1'),
            value: 'DESC',
            selected: true
        },
        {
            label: t('pageNews.sort.option2'),
            value: 'ASC',
            selected: false
        }
    ]);

    const processedNewsArticles = computed(() => {
        const list = (newsData.value ?? [])
            .map((news) => normalizeNewsCardStory(news, referencedNewsData.value ?? []))
            .filter((story): story is NonNullable<typeof story> => Boolean(story));

        const translatedCategory = selectedCategory.value
            ? categoryTranslations.value.get(selectedCategory.value) || selectedCategory.value
            : null;

        const resolveCategoryKey = (category: string | number) => {
            const matched = datasourceEntries.value.find((data) => data.value === category);
            return matched ? matched.dimension_value || matched.value : category;
        };

        const filtered = translatedCategory
            ? list.filter((news) =>
                  (news.content?.Categories ?? []).some(
                      (category: string | number) => resolveCategoryKey(category) === translatedCategory // Compare translated categories
                  )
              )
            : list;

        const sortedNewsData = filtered.toSorted((a, b) => {
            const dateA = new Date(a.content?.Date ?? 0).getTime();
            const dateB = new Date(b.content?.Date ?? 0).getTime();
            return sortOrder.value === 'ASC' ? dateA - dateB : dateB - dateA;
        });

        return sortedNewsData.map((news) => ({
            ...news,
            parsedAbstract: renderToString(news.content?.Abstract ?? EMPTY_RICHTEXT)
        }));
    });

    const selectedCategoryTranslation = computed(() => {
        if (!selectedCategory.value) return null;
        return categoryTranslations.value.get(selectedCategory.value as string) || (selectedCategory.value as string);
    });
</script>

<template>
    <SidebarGrid
        :title="props.blok.title"
        :mood-image="props.blok.moodImage"
        :show-cta="false"
        :sidebar-button-open-text="t('pageNews.sidebarButtonOpenText')"
        :sidebar-button-closed-text="t('pageNews.sidebarButtonClosedText')"
        :sidebar-title="props.blok.sidebarTitle || ''"
    >
        <template #sidebar>
            <SidebarCategories :category-type="'news'" />
        </template>

        <template #user-actions>
            <div class="select-element">
                <SelectInput
                    id="sort-select"
                    v-model="sortOrder"
                    :options="sortingOptions"
                    :label="`${t('pageNews.sort.sortBy')}`"
                    :aria-label="t('pageNews.sort.sortBy')"
                />
            </div>
        </template>

        <template #content>
            <div
                v-if="selectedCategory"
                class="category-info"
            >
                <p>
                    {{ t('pageNews.selectedCategory') }}:
                    <strong>{{ selectedCategoryTranslation }}</strong>
                </p>
                <button @click="router.push({ query: {} })">
                    {{ t('pageNews.clearCategory') }}
                </button>
            </div>
            <div class="news-grid">
                <NewsCard
                    v-for="news in processedNewsArticles"
                    :key="news.uuid"
                    :news="news"
                    :is-overview-page="true"
                    :categories="datasourceEntries"
                />

                <div
                    v-if="processedNewsArticles.length === 0"
                    class="no-news-container"
                >
                    <img
                        src="../../assets/images/404.svg"
                        :alt="t('general.error')"
                    />
                    <h2>{{ t('pageNews.error.header') }}</h2>
                    <p>
                        {{ t('pageNews.error.text') }}
                    </p>
                </div>
            </div>
        </template>
    </SidebarGrid>
</template>

<style scoped>
    @import '~/assets/scss/pages/page-news-overview.scss';
    @import '~/assets/scss/pages/utils/shared-components.scss';
</style>
